import { useState } from 'react';

import { ModalBase } from './ModalBase';

import type React from 'react';

export interface DevolverAtendenteModalProps {
  open: boolean;
  /** Nome do atendente original (null degrada honesto — sem "Carolina" hardcoded). */
  attendantName: string | null;
  isPending: boolean;
  /** true quando o último envio falhou — mostra um `role=alert`. */
  failed: boolean;
  /** Desfecho honesto derivado da RESPOSTA do servidor (null enquanto não houver). */
  outcome: string | null;
  onClose: () => void;
  onConfirm: (returnReason: string) => void;
}

/** Piso de UI mais estrito que o backend (return exige ≥10) — coerente com a Tela 2. */
const REASON_FLOOR = 20;

/**
 * DevolverAtendenteModal — converte modals-3.jsx:522 (DevolverAtendenteModal).
 * Devolve a conversa ao atendente original (`POST takeovers/:id/return {returnReason}`).
 * O motivo é OBRIGATÓRIO (o backend exige; o mockup rotulava "opcional"). As checkboxes
 * `markFeedback`/`notifyGestor` do protótipo NÃO foram portadas — não há backend (seria
 * affordance falsa). Guard anti duplo-submit; desfecho vem da RESPOSTA do servidor.
 */
export function DevolverAtendenteModal({
  open,
  attendantName,
  isPending,
  failed,
  outcome,
  onClose,
  onConfirm,
}: DevolverAtendenteModalProps): React.JSX.Element | null {
  const [reason, setReason] = useState('');
  if (!open) return null;
  const trimmedLen = reason.trim().length;
  const canSubmit = trimmedLen >= REASON_FLOOR && !isPending;

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Devolver atendente"
      width={500}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button className="btn-ghost-mini" type="button" onClick={onClose} disabled={isPending}>
            Cancelar
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              onConfirm(reason);
            }}
          >
            {isPending ? 'Devolvendo…' : 'Devolver e sair'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <p style={{ fontSize: 'var(--text-sm)' }}>
          Devolver a conversa{attendantName !== null ? ' para ' : ''}
          {attendantName !== null && <b>{attendantName}</b>}? A conversa volta ao atendente
          original.
        </p>
        <label className="modal-form__field">
          <span className="modal-form__label">
            Motivo da devolução (obrigatório · mínimo {REASON_FLOOR} caracteres)
          </span>
          <textarea
            className="modal-form__textarea"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            rows={3}
            placeholder="Esclareci a questão técnica com o cliente — bom retomar daqui…"
            aria-label="Motivo da devolução"
          />
          <span className="modal-form__hint">
            {trimmedLen}/{REASON_FLOOR} mínimo
          </span>
        </label>
        <div className="modal-form__callout modal-form__callout--warn">
          ⚠ O audit registra a devolução com o motivo obrigatório.
        </div>
        {failed && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">Não foi possível devolver a conversa.</span>
            </div>
          </div>
        )}
        {outcome !== null && (
          <div className="modal-form__callout" role="status">
            {outcome}
          </div>
        )}
      </div>
    </ModalBase>
  );
}
